/* eslint-disable @stylistic/no-multiple-empty-lines */
import { ErrorMessage, TaAutocomplete, TaInput } from '@components/shared';

import { MuiPhone } from '@components/shared/PhoneInput/PhoneInput';
import { StepNames } from '@features/auth/registration/registration.types';
import useLanguageNavigate from '@hooks/useLanguageNavigate';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import Button from '@mui/material/Button';
import { validatePhoneNumberByCountryCode } from '@utils/phoneValidation';
import { Dictionary, Nullable } from '@utils/util.types';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountries, useCountriesStore } from 'services/countries';
import { CreateUserPayload, useCreateUser, useUpdateUser, useUsersStore } from 'services/users';
import * as Yup from 'yup';
import '../styles.css';

interface CompanyPersonalDetails {
	companyName: string;
	companyId: string;
	first_name: string;
	last_name: string;
	phone: string;
	country: Nullable<Dictionary>;
	is_company: boolean;
}

const initialValues: CompanyPersonalDetails = {
	companyName: '',
	companyId: '',
	first_name: '',
	last_name: '',
	phone: '',
	country: null,
	is_company: true,
};

export function CompanyStepPersonalDetails() {
	const { navigateWithLanguage } = useLanguageNavigate();
	const { t } = useTranslation();
	const createUserHandler = useCreateUser();
	const updateUserHandler = useUpdateUser();
	const { user } = useUsersStore();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [phoneValidationError, setPhoneValidationError] = useState<string>('');

	const personalDetailsValidationSchema = Yup.object().shape({
		companyName: Yup.string().required('fieldNameError'),
		companyId: Yup.string().required('fieldNameError'),
		first_name: Yup.string().required('fieldNameError'),
		last_name: Yup.string().required('fieldNameError'),
		phone: Yup.string()
			.required('fieldNameError')
			.test('phone-validation', t('phoneNumberMatchError'), function (value) {
				if (!value) return false;

				const validation = validatePhoneNumberByCountryCode(value, t);
				if (!validation.isValid) {
					return this.createError({ message: validation.errorMessage });
				}
				return true;
			}),
		country: Yup.object().required('fieldNameError'),
	});

	// useUser();
	const formik = useFormik<CompanyPersonalDetails>({
		initialValues,
		validationSchema: personalDetailsValidationSchema,
		onSubmit: async (values) => {
			try {
				setIsSubmitting(true);

				// Final validation before submission
				const phoneValidation = validatePhoneNumberByCountryCode(values.phone, t);
				if (!phoneValidation.isValid) {
					setPhoneValidationError(phoneValidation.errorMessage || t('phoneNumberMatchError'));
					setIsSubmitting(false);
					return;
				}

				const { companyName, companyId, ...restValues } = values;

				const formattedValue = {
					...restValues,
					companies: [
						{
							company_name: companyName,
							company_id: companyId.toString(),
						},
					],
					country: values.country?.id,
				};

				if (user?.id) {
					await updateUserHandler(formattedValue);
				} else {
					await createUserHandler(formattedValue as unknown as CreateUserPayload);
				}

				navigateWithLanguage('/company/resume', {
					state: { step: StepNames.RESUME },
				});
			} catch (error) {
				console.error(error);
			} finally {
				setIsSubmitting(false);
			}
		},
	});
	const { setValues } = formik;
	const { countries } = useCountriesStore();

	useCountries();

	// Handle phone number change with real-time validation
	const handlePhoneChange = (phone: string) => {
		formik.setFieldValue('phone', phone);
		setPhoneValidationError('');

		// Real-time validation based on phone number's country code
		if (phone) {
			const validation = validatePhoneNumberByCountryCode(phone, t);
			if (!validation.isValid) {
				setPhoneValidationError(validation.errorMessage || t('phoneNumberMatchError'));
			}
		}
	};

	// Handle country change and re-validate phone if it exists
	const handleCountryChange = (event: any, value: any) => {
		formik.setFieldValue('country', value);
		setPhoneValidationError('');

		// Re-validate phone number (validation is independent of country dropdown)
		if (formik.values.phone) {
			const validation = validatePhoneNumberByCountryCode(formik.values.phone, t);
			if (!validation.isValid) {
				setPhoneValidationError(validation.errorMessage || t('phoneNumberMatchError'));
			}
		}
	};

	useEffect(() => {
		if (user) {
			setValues({
				companyName: user.company?.company_name,
				companyId: user.company?.company_id,
				first_name: user.first_name,
				last_name: user.last_name,
				phone: user.phone,
				country: countries.find(
					(country) => country.id === user.country || country.name === user.country
				),
				is_company: true,
			});
		}
	}, [countries, setValues, user]);

	return (
		<div className="company-page">
			<div className="company-container">
				<div className="company-form">
					<div className="form-content">
						<p className="page-title text-base text-dark font-bold">{t('personalDetails')}</p>

						<form
							onSubmit={formik.handleSubmit}
							className="mt-[20px] flex flex-col gap-[20px] pb-5"
						>
							<div>
								<TaInput
									icon={<CorporateFareIcon sx={{ color: '#7B809A' }} />}
									id={'companyName'}
									label={t('companyName')}
									size={'small'}
									value={formik.values.companyName}
									onChange={formik.handleChange}
									error={formik.errors.companyName}
									touched={formik.touched.companyName}
									isRequired
								/>
								{formik.touched.companyName && formik.errors.companyName && (
									<ErrorMessage
										message={t(formik.errors.companyName || '', {
											fieldName: t('companyName'),
										})}
									/>
								)}
							</div>

							<div>
								<TaInput
									icon={<CorporateFareIcon sx={{ color: '#7B809A' }} />}
									id={'companyId'}
									label={t('companyIdNumber')}
									size={'small'}
									value={formik.values.companyId}
									onChange={formik.handleChange}
									error={formik.errors.companyId}
									touched={formik.touched.companyId}
									isRequired
								/>
								{formik.touched.companyId && formik.errors.companyId && (
									<ErrorMessage
										message={t(formik.errors.companyId || '', {
											fieldName: t('companyIdNumber'),
										})}
									/>
								)}
							</div>

							<div>
								<TaInput
									icon={
										<PersonOutlineOutlinedIcon
											sx={{
												color:
													formik.touched.first_name && formik.errors.first_name
														? '#FD3333'
														: '#7B809A',
											}}
										/>
									}
									id={'first_name'}
									label={t('representativeFirstName')}
									size={'small'}
									value={formik.values.first_name}
									onChange={formik.handleChange}
									error={formik.errors.first_name}
									touched={formik.touched.first_name}
									isRequired
								/>
								{formik.touched.first_name && formik.errors.first_name && (
									<ErrorMessage
										message={t(formik.errors.first_name || '', {
											fieldName: t('representativeFirstName'),
										})}
									/>
								)}
							</div>

							<div>
								<TaInput
									icon={
										<PersonOutlineOutlinedIcon
											sx={{
												color:
													formik.touched.last_name && formik.errors.last_name
														? '#FD3333'
														: '#7B809A',
											}}
										/>
									}
									id={'last_name'}
									label={t('representativeLastName')}
									size={'small'}
									value={formik.values.last_name}
									onChange={formik.handleChange}
									error={formik.errors.last_name}
									touched={formik.touched.last_name}
									isRequired
								/>
								{formik.touched.last_name && formik.errors.last_name && (
									<ErrorMessage
										message={t(formik.errors.last_name || '', {
											fieldName: t('representativeLastName'),
										})}
									/>
								)}
							</div>

							<div>
								<MuiPhone
									className="w-full"
									name="phone"
									placeholder={t('representativePhoneNumber')}
									value={formik.values.phone}
									onChange={handlePhoneChange}
									onBlur={formik.handleBlur}
									error={
										(formik.touched.phone && Boolean(formik.errors.phone)) ||
										Boolean(phoneValidationError)
									}
								/>
								{((formik.touched.phone && formik.errors.phone) || phoneValidationError) && (
									<ErrorMessage
										message={
											phoneValidationError ||
											t(formik.errors.phone || 'phoneNumberError', {
												fieldName: t('representativePhoneNumber'),
											})
										}
									/>
								)}
							</div>

							<div>
								<TaAutocomplete
									required
									hasFlag
									options={countries}
									getOptionLabel={(option) => option.name}
									label={t('country')}
									value={formik.values.country as Dictionary}
									onChange={handleCountryChange}
									error={Boolean(formik.touched.country && formik.errors.country)}
								/>
								{formik.touched.country && formik.errors.country && (
									<ErrorMessage
										message={t(formik.errors.country as string, { fieldName: t('country') })}
									/>
								)}
							</div>

							<Button
								color="primary"
								variant="contained"
								fullWidth
								type="submit"
								loading={isSubmitting}
							>
								{t('next')}
								<KeyboardArrowRightIcon />
							</Button>
						</form>
					</div>
				</div>
			</div>
		</div>
	);
}
